/**
 * Фильтры каталога в адресной строке.
 *
 * Состояние фильтров живёт в URL, а не в компоненте: ссылку на «нерешённые
 * средние по соединениям» должно быть можно сохранить и переслать
 * (docs/10-ui-ux.md).
 */

import { TASK_STATUS_FILTERS } from '@/api/catalog'
import type { Difficulty, TaskFilters, TaskStatusFilter } from '@/api/catalog'

const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard']
const STATUSES = TASK_STATUS_FILTERS

export type { TaskStatusFilter }

function readEnum<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
): T | undefined {
  const value = params.get(key)
  return allowed.find((item) => item === value)
}

export function parseFilters(params: URLSearchParams): TaskFilters {
  const page = Number(params.get('page') ?? '1')
  return {
    topic: params.get('topic') ?? undefined,
    difficulty: readEnum(params, 'difficulty', DIFFICULTIES),
    status: readEnum(params, 'status', STATUSES),
    tag: params.get('tag') ?? undefined,
    page: Number.isFinite(page) && page > 1 ? page : undefined,
  }
}

export function filtersToParams(filters: TaskFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.topic) params.set('topic', filters.topic)
  if (filters.difficulty) params.set('difficulty', filters.difficulty)
  if (filters.status) params.set('status', filters.status)
  if (filters.tag) params.set('tag', filters.tag)
  // Первая страница в адресе не отражается: `?page=1` — лишний шум в ссылке.
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  return params
}

export function hasAnyFilter(filters: TaskFilters): boolean {
  return Boolean(filters.topic ?? filters.difficulty ?? filters.status ?? filters.tag)
}

/** Изменение одного фильтра сбрасывает страницу: иначе выборка «съезжает». */
export function withFilter<K extends keyof TaskFilters>(
  filters: TaskFilters,
  key: K,
  value: TaskFilters[K],
): TaskFilters {
  return { ...filters, [key]: value, page: undefined }
}
